import React, { useState } from 'react';
import { inviteHelper, removeInvitation } from '../../utils/tripPlanUtils';

const SimpleInviteModal = ({ isOpen, onClose, tripPlanId, tripName }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('กรุณากรอกอีเมล');
      return;
    }

    if (!email.includes('@')) {
      setError('รูปแบบอีเมลไม่ถูกต้อง');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // ลองลบคำเชิญเก่าก่อน (ถ้ามี)
      try {
        await removeInvitation(tripPlanId, email.trim());
        console.log('🗑️ Removed old invitation for:', email.trim());
      } catch (removeErr) {
        // ไม่เป็นไรถ้าลบไม่ได้ (อาจจะไม่มีคำเชิญเก่า)
        console.log('ℹ️ No old invitation to remove for:', email.trim());
      }
      
      // เรียกใช้ inviteHelper จริง
      const result = await inviteHelper(tripPlanId, email.trim());
      
      setSuccess(true);
      setEmail('');
      
      // แสดง token ใน console และ alert
      if (result && result.token) {
        console.log('🎉 Invitation sent successfully!');
        console.log('📧 Email:', email.trim());
        console.log('🔑 Token:', result.token);
        console.log('🔗 URL:', `http://localhost:3000/invite/${result.token}`);
        alert(`ส่งคำเชิญสำเร็จ!\n\nToken: ${result.token}\n\nURL: http://localhost:3000/invite/${result.token}`);
      }
      
      // Auto close after 5 seconds (เพิ่มเวลาให้อ่าน token)
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 5000);
      
    } catch (err) {
      setError(err.message || 'เกิดข้อผิดพลาดในการส่งคำเชิญ');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-xl">👥</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">เชิญเพื่อนมาช่วยจัดทริป</h3>
              <p className="text-sm text-gray-500">{tripName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-green-600 text-3xl">✅</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">ส่งคำเชิญสำเร็จ!</h4>
              <p className="text-gray-600">
                ได้ส่งคำเชิญไปยัง <span className="font-medium">{email}</span> แล้ว
              </p>
              <p className="text-sm text-gray-500 mt-2">
                เพื่อนจะได้รับอีเมลพร้อมลิงก์สำหรับเข้าร่วมทริป
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  อีเมลเพื่อน
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-400 text-lg">📧</span>
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="friend@example.com"
                    disabled={loading}
                  />
                </div>
                {error && (
                  <p className="text-red-500 text-sm mt-1">{error}</p>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-blue-900 mb-2">สิทธิ์ที่เพื่อนจะได้รับ:</h5>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• ช่วยทำ checklist ทริป</li>
                  <li>• จองคิวน้องแทนคุณ</li>
                  <li>• ดูข้อมูลทริปทั้งหมด</li>
                </ul>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'กำลังส่ง...' : 'ส่งคำเชิญ'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleInviteModal;
